import { inject, Injectable, type Renderer2 } from '@angular/core';
import {
  AppThemeConfig,
  type ColorResource,
  FontHelper,
  Fonts,
  FontSizeResourceKeys,
  FontWeightResourceKeys,
  type ThemeStyle
} from '../../models/app-theme-config.model';
import { ConfigResourceService } from '../../services/config-resource.service';

@Injectable({
  providedIn: 'root'
})
export class AppThemeService {
  private readonly configService = inject(ConfigResourceService);
  private readonly stylesTagId: string = 'Spa_Styles';

  getFont(): Fonts {
    const siteFont: Fonts = this.configService.getProcessedResourceValue<Fonts>('Spa_Style_Font', Fonts.DEFAULT);
    return siteFont;
  }

  assignAttributesToClasses(resource: ThemeStyle): Record<string, string[]> {
    const classes: Record<string, string[]> = {
      [resource.className]: []
    };
    for (const [attribute, value] of Object.entries(resource.styling)) {
      if ((typeof value === 'string' && value) || typeof value === 'number' || typeof value === 'boolean') {
        classes[resource.className].push(`${attribute}: ${value};`);
      } else if (value !== null && typeof value === 'object') {
        for (const [selector, pseudoValue] of Object.entries(value)) {
          let classSelector: string = '';
          if (selector === 'default') {
            classSelector = resource.className;
          } else if (!Object.prototype.hasOwnProperty.call(classes, selector)) {
            classSelector = `${resource.className}:${selector}`;
            if (!Array.isArray(classes[classSelector])) {
              classes[classSelector] = [];
            }
          }
          classes[`${classSelector}`].push(`${attribute}: ${pseudoValue};`);
        }
      }
    }
    return classes;
  }

  createClassFromColorResource(color: ColorResource): string {
    // eh probably makes sense to have these support only text color and background color, more can be added later, but I
    // don't think we will need more.
    return ['Background', 'Color', 'Border']
      .map(attribute => `.${color.resourceKey}_${attribute} {${attribute.toLowerCase()}: ${color.value};}`)
      .join(' ');
  }

  createClassFromFontSize(fontSize: string, compact: boolean): string {
    return `.${fontSize}_Font{${FontHelper.getFontSize(fontSize, compact)}}`;
  }

  createClassFromFontWeight(weight: keyof typeof FontHelper.DEFAULT_FONT_WEIGHT): string {
    // our default font weight is controlled by the font family. This method will need to be updated
    // when adding new fonts
    return `.${weight}{ ${`font-family: ${FontHelper.DEFAULT_FONT_WEIGHT[weight]};`}}`;
  }

  // by default our buttons will use common colors from the palette however, clients can override just buttons (primary and secondary)
  // with a single string key.
  getOverridesForButtons(resource: ThemeStyle): ThemeStyle {
    let buttonColor: string = this.configService.getProcessedResourceValue<string>(resource.resourceKey);

    if (buttonColor) {
      const forcedColorByConfig = this.configService.getProcessedResourceValue<string>('Spa_Site_ForceColor_Value', '');
      buttonColor = buttonColor.startsWith('#') ? buttonColor : `#${buttonColor}`;
      buttonColor = forcedColorByConfig || buttonColor;
      resource.styling = {
        ...resource.styling,
        'background-color': {
          default: buttonColor,
          hover: AppThemeConfig.adjustColor(buttonColor, -30, true)
        },
        'border-color': {
          default: AppThemeConfig.adjustColor(buttonColor, -50, true),
          hover: AppThemeConfig.adjustColor(buttonColor, -50, true)
        }
      };
    }

    return resource;
  }

  createClassFromResource(resource: ThemeStyle): string {
    // grabs all the styling resources
    const classes: Record<string, string[]> = this.assignAttributesToClasses(resource);
    // grabs the font size/family
    classes[resource.className] = [FontHelper.getFontFamily(resource), ...classes[resource.className]];
    if (resource.fontSize) {
      const fontSize: string | null = FontHelper.getFontForResourceSizes(resource, AppThemeConfig.compact);
      classes[resource.className] = [...(fontSize ? [fontSize] : []), ...classes[resource.className]];
    }

    const createdClasses = [];
    for (const [className, values] of Object.entries(classes)) {
      const createdClass = `.${className} { ${values.join(' ')} }`;
      createdClasses.push(createdClass);
    }

    return createdClasses.join(' ');
  }

  getOverridesForClasses(defaults: ThemeStyle[], compact: boolean = false): ThemeStyle[] {
    return defaults.map(resource => {
      const desktopOverride = this.configService.getProcessedResourceValue<any>(resource.resourceKey);
      let override = compact ? this.configService.getProcessedResourceValue<any>(`${resource.resourceKey}_Mobile`) : desktopOverride;
      override = compact && !override ? desktopOverride : override;

      resource.styling = { ...resource.styling, ...override };
      return resource;
    });
  }

  getOverridesForColorResources(colorResources: ColorResource[]): ColorResource[] {
    const forcedColorByConfig = this.configService.getProcessedResourceValue<string>('Spa_Site_ForceColor_Value', '');
    const colorResource = colorResources.map(color => {
      let colorValue: string = this.configService.getProcessedResourceValue<string>(color.resourceKey, color.value);
      colorValue = forcedColorByConfig || colorValue;

      return {
        ...color,
        value: colorValue.startsWith('#') ? colorValue : `#${colorValue}`
      };
    });
    return colorResource;
  }

  initCreateAppTheme(renderer: Renderer2, compact: boolean = false): void {
    AppThemeConfig.compact = compact;
    AppThemeConfig.font = this.getFont();
    AppThemeConfig.setStaticVariables(renderer);
    AppThemeConfig.colorResources = this.getOverridesForColorResources(AppThemeConfig.colorResources);
    AppThemeConfig.resources = AppThemeConfig.setResources(renderer);
    // check to see if we have any overrides in our config
    const resourceOverrides = this.getOverridesForClasses(
      AppThemeConfig.resources.filter(resource => !resource.isButton),
      compact
    );
    const buttonOverrides = AppThemeConfig.resources
      .filter(resource => resource.isButton)
      .map(button => this.getOverridesForButtons(button));

    AppThemeConfig.resources = [...resourceOverrides, ...buttonOverrides];

    // create a style document if it doesn't exist already, otherwise update the one in use.
    let applicationStyles: HTMLElement | null = null;
    try {
      applicationStyles = renderer.selectRootElement(`#${this.stylesTagId}`, true);
    } catch {
      // Renderer2.selectRootElement() always throws NG05104 when the element isn't found.
    }
    if (!applicationStyles) {
      const headElement: HTMLElement = renderer.selectRootElement('head', true);
      applicationStyles = renderer.createElement('style');
      renderer.setProperty(applicationStyles, 'id', this.stylesTagId);
      renderer.appendChild(headElement, applicationStyles);
    }

    const configuredFontClasses = Object.values(FontSizeResourceKeys).map(size => this.createClassFromFontSize(size, compact));
    const configuredWeightClasses = Object.values(FontWeightResourceKeys).map(weight => this.createClassFromFontWeight(weight));
    const configuredColorClasses = AppThemeConfig.colorResources.map(color => this.createClassFromColorResource(color));
    const configuredClasses = AppThemeConfig.resources.map(resource => this.createClassFromResource(resource));
    const appThemeCSS = [...configuredClasses, ...configuredColorClasses, ...configuredFontClasses, ...configuredWeightClasses].join(' ');
    const customCSS = this.configService.getProcessedResourceValue<string>('CustomCSS', '');
    renderer.setProperty(applicationStyles, 'innerHTML', `${appThemeCSS} ${customCSS}`);
  }
}
